import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, Phone, X } from "lucide-react";
import { Logo } from "./Logo";
import { useLang } from "../i18n";

export const Navbar = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const LINKS = [
    { href: "/#services", label: t("nav.services") },
    { href: "/#tarifs", label: t("nav.pricing") },
    { href: "/#flotte", label: t("nav.fleet") },
    { href: "/#avis", label: t("nav.reviews") },
    { href: "/blog", label: t("nav.blog") },
    { href: "/#contact", label: t("nav.contact") },
  ];

  const LangSwitch = () => (
    <div className="flex items-center rounded-full border border-slate-200 bg-white p-0.5" data-testid="lang-switch">
      {["fr", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}-button`}
          onClick={() => setLang(l)}
          className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider transition-colors ${
            lang === l ? "bg-navy text-white" : "text-navy/50 hover:text-navy"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );

  return (
    <header
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled || open
          ? "border-b border-slate-200/60 bg-white/85 shadow-sm backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="nav-logo-link" aria-label="CabNow — accueil">
          <Logo />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-links">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-sm font-medium text-navy/70 transition-colors hover:text-azure"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <LangSwitch />
          <a
            href="tel:+33661297599"
            data-testid="nav-phone-link"
            className="flex items-center gap-2 text-sm font-semibold text-navy/80 transition-colors hover:text-azure"
          >
            <Phone size={16} /> +33 6 61 29 75 99
          </a>
          <Link
            to="/reservation"
            data-testid="nav-book-button"
            className="rounded-full bg-azure px-6 py-2.5 text-sm font-bold text-white shadow-lg shadow-azure/25 transition-[background-color,transform] hover:bg-azure-hover hover:-translate-y-0.5"
          >
            {t("nav.book")}
          </Link>
        </div>

        <div className="flex items-center gap-3 lg:hidden">
          <LangSwitch />
          <button
            data-testid="nav-mobile-toggle"
            className="rounded-lg p-2 text-navy"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-slate-100 bg-white/95 px-6 py-6 backdrop-blur-xl lg:hidden" data-testid="nav-mobile-menu">
          <div className="flex flex-col gap-4">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-base font-medium text-navy/80"
              >
                {l.label}
              </a>
            ))}
            <Link
              to="/reservation"
              onClick={() => setOpen(false)}
              className="mt-2 rounded-full bg-azure px-6 py-3 text-center text-sm font-bold text-white"
            >
              {t("nav.book")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
